import React, { useState, useEffect, useMemo } from "react";
import Card from "components/card";
import Modal from "components/modal/Modal";
import { useItens } from "context/ItensContext";
import { useUnidades } from "context/UnidadeContext";
import { useDevices } from "context/DeviceContext";
import { useSelectedDevice } from "context/SelectedDeviceContext";
import { MdAdd, MdDeleteOutline } from "react-icons/md";

interface ItensDisponiveisProps {
  selectedUnidadeId: string | null;
}

export default function ItensDisponiveis({
  selectedUnidadeId,
}: ItensDisponiveisProps) {
  const { itens, adicionarItem, removerItem, atualizarStatusItem, fetchItens } = useItens();
  const { unidades } = useUnidades();
  const { devices } = useDevices();
  const { selectedDeviceId } = useSelectedDevice();
  const [deleteMode, setDeleteMode] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [selectedUnidades, setSelectedUnidades] = useState<string[]>([]);

  useEffect(() => {
    if (selectedDeviceId) {
      fetchItens(selectedDeviceId);
    } else {
      fetchItens();
    }
  }, [selectedDeviceId, fetchItens]);

  const handleAddItem = async () => {
    if (!newItemName.trim() || selectedUnidades.length === 0) {
      alert("Preencha o nome e selecione pelo menos uma unidade.");
      return;
    }

    for (const unidadeId of selectedUnidades) {
      const unidade = unidades.find(u => u.id === unidadeId);
      if (unidade && unidade.dispositivoId) {
        const newItem = {
          nome: newItemName,
          dataAdicao: new Date().toLocaleDateString("pt-BR"),
          dispositivoId: unidade.dispositivoId,
          disponivel: true,
        };
        await adicionarItem(newItem);
      }
    }

    setShowAddModal(false);
    setNewItemName("");
    setSelectedUnidades([]);
  };

  const handleDeleteItem = async (id: string) => {
    const confirmDelete = window.confirm("Tem certeza que deseja apagar este item?");
    if (confirmDelete) {
      await removerItem(id);
    }
  };

  const handleCheckbox = async (id: string, currentStatus: boolean) => {
    await atualizarStatusItem(id, !currentStatus);
  };

  const itensOrdenadosEFiltrados = useMemo(() => {
    const itensFiltrados = selectedUnidadeId
      ? itens.filter((item) => {
          const unidadeSelecionada = unidades.find(u => u.id === selectedUnidadeId);
          return unidadeSelecionada && item.dispositivoId === unidadeSelecionada.dispositivoId;
        })
      : itens;

    const parseDate = (dateStr: string) => {
      if (!dateStr || dateStr.length < 10) return new Date(0);
      const [day, month, year] = dateStr.split('/').map(Number);
      return new Date(year, month - 1, day);
    };

    return [...itensFiltrados].sort((a, b) => {
      const dateA = parseDate(a.dataAdicao);
      const dateB = parseDate(b.dataAdicao);
      return dateB.getTime() - dateA.getTime();
    });
  }, [itens, selectedUnidadeId, unidades]);

  return (
    <Card extra="w-full p-4 h-full">
      <div className="mb-4 flex justify-between items-center shrink-0">
        <h4 className="text-xl font-bold text-navy-700">
          Itens Disponíveis
        </h4>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1 rounded bg-green-500 px-3 py-1 text-white hover:bg-green-600"
          >
            <MdAdd /> Adicionar
          </button>
          <button
            onClick={() => setDeleteMode(!deleteMode)}
            className={`flex items-center gap-1 rounded px-3 py-1 text-white ${
              deleteMode ? "bg-red-500 hover:bg-red-600" : "bg-gray-500 hover:bg-gray-600"
            }`}
          >
            <MdDeleteOutline /> {deleteMode ? "Cancelar" : "Apagar"}
          </button>
        </div>
      </div>

      {itensOrdenadosEFiltrados.length === 0 ? (
        <div className="flex items-center justify-center h-full text-gray-500 text-center">
          {selectedUnidadeId 
            ? "Nenhum item encontrado para esta unidade." 
            : "Nenhum item encontrado. Adicione um para começar."
          }
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[280px]">
          <table className="w-full text-left">
            <thead>
              <tr className="text-sm font-bold text-gray-600">
                <th>Nome do Produto</th>
                <th>Data Adição</th>
                {deleteMode && <th>Ação</th>}
              </tr>
            </thead>
            <tbody>
              {itensOrdenadosEFiltrados.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="flex items-center gap-2 py-2">
                    <input
                      type="checkbox"
                      checked={item.disponivel}
                      onChange={() => handleCheckbox(item.id!, item.disponivel)}
                      className="defaultCheckbox relative flex h-[20px] min-h-[20px] w-[20px] min-w-[20px] appearance-none items-center justify-center rounded-md border border-gray-300 text-white/0 outline-none transition duration-200 checked:border-none checked:text-white hover:cursor-pointer checked:bg-[#CF4949]"
                    />
                    <span className="text-sm font-bold text-navy-700">
                      {item.nome}
                    </span>
                  </td>
                  <td className="text-sm font-bold text-navy-700">
                    {item.dataAdicao}
                  </td>
                  {deleteMode && (
                    <td>
                      <button
                        className="text-sm font-bold text-red-500"
                        onClick={() => handleDeleteItem(item.id!)}
                      >
                        Apagar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <Modal onClose={() => setShowAddModal(false)}>
          <h2 className="text-lg font-bold mb-2">Adicionar Item</h2>
          <input
            className="w-full border rounded p-2 mb-2"
            placeholder="Nome do Produto"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
          />
          <label className="block font-medium mb-1">
            Selecionar Unidades:
          </label>
          <div className="flex flex-col gap-1 mb-4 max-h-40 overflow-y-auto">
            {unidades.map((unidade) => {
              const device = devices.find(d => d.id === unidade.dispositivoId);
              return (
                <label key={unidade.id} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="defaultCheckbox relative flex h-[20px] min-h-[20px] w-[20px] min-w-[20px] appearance-none items-center 
      justify-center rounded-md border border-gray-300 text-white/0 outline-none transition duration-200
      checked:border-none checked:text-white hover:cursor-pointer checked:bg-[#CF4949]"
                    checked={selectedUnidades.includes(unidade.id)}
                    onChange={() => {
                      setSelectedUnidades((prev) =>
                        prev.includes(unidade.id)
                          ? prev.filter((id) => id !== unidade.id)
                          : [...prev, unidade.id]
                      );
                    }}
                  />
                  <span className="text-sm">
                    {device?.name || unidade.nome}
                  </span>
                </label>
              );
            })}
          </div>
          <button
            className="mt-2 px-3 py-1 bg-[#CF4949] font-bold text-white transition duration-200 hover:bg-[#B84343] active:bg-[#B84343] rounded"
            onClick={handleAddItem}
          >
            Adicionar
          </button>
        </Modal>
      )}
    </Card>
  );
}
